<template>
  <div class="border border-gray-200 bg-white p-4 rounded-lg">
    <div class="w-10 h-10 inline-flex items-center justify-center mb-4">
      <img
        style="width: 40px; height: 40px; opacity: 1"
        :src="logoUrl">
    </div>
    <h2 class="text-lg font-bold title-font">
      {{ examination.title }}
    </h2>
    <p
      v-if="!examination.discount_price_id"
      class="leading-relaxed text-base mb-2">
      (通常:{{ formatPrice(examination.price) }}円)
    </p>
    <p
      v-else-if="!examination.is_subscription_item"
      class="leading-relaxed text-base mb-2">
      (通常:{{ formatPrice(examination.price) }}円)
    </p>
    <p
      v-else
      class="leading-relaxed text-base mb-2">
      (どうぶつ看護ポータル サブスクリプション契約者限定)
    </p>

    <!-- 購入期間 -->
    <div class="overflow-hidden sm:rounded-none py-4">
      <h2>購入期間</h2>
      <p class="ml-2">
        <span class="inline-block">{{ formatDate(examination.reception_start) }} 〜</span>
        <span class="inline-block">{{ formatDate(examination.reception_end) }}</span>
      </p>
    </div>

    <!-- リファクタリング 2024年度以前の表示 この変数名を変更 -->
    <div
      v-if="fiscalYearStart(examination.reception_start)"
      class="items-center justify-between overflow-hidden sm:rounded-none pb-4">
      <h2>実施期間(集計対象)</h2>
      <p class="ml-2">
        <span class="inline-block">{{ formatDate(examination.start_date) }} 〜</span>
        <span class="inline-block">{{ formatDate(examination.end_date) }}</span>
      </p>
    </div>
    <div
      v-if="examination.result_announcement_date && fiscalYearStart(examination.reception_start)"
      class="items-center justify-between overflow-hidden sm:rounded-none">
      <h2>実施期間(集計対象外)</h2>
      <p class="ml-2">
        <span class="inline-block">{{ formatDate(examination.result_announcement_date) }} 〜</span>
        <span class="inline-block">{{ formatDate(examination.reception_end) }}</span>
      </p>
    </div>

    <!-- リファクタリング 2025年度以降の表示 -->
    <div
      v-if="!fiscalYearStart(examination.reception_start)"
      class="items-center justify-between overflow-hidden sm:rounded-none pb-4">
      <h2>実施期間</h2>
      <p class="ml-2">
        <span class="inline-block">{{ formatDate(examination.start_date) }} 〜</span>
        <span class="inline-block">{{ formatDate(examination.reception_end) }}</span>
      </p>
    </div>
    <div
      v-if="!fiscalYearStart(examination.reception_start)"
      class="items-center justify-between overflow-hidden sm:rounded-none">
      <h2>成績集計開始日時</h2>
      <p class="ml-2">
        <span class="inline-block">{{ formatDate(examination.result_announcement_date) }} 〜</span>
      </p>
    </div>

    <div class="flex items-center justify-end overflow-hidden sm:rounded-none p-2">
      <button
        v-if="isPurchased(examination.id)"
        class="mt-2 px-8 py-2 rounded bg-red-500 text-blue-50 max-w-max shadow-sm hover:opacity-50 opacity-50 cursor-not-allowed">
        購入済み
      </button>
      <button
        v-else-if="isOutOfTerm()"
        class="mt-2 px-8 py-2 rounded bg-gray-600 text-blue-50 max-w-max shadow-sm hover:opacity-50 opacity-50 cursor-not-allowed">
        購入期間外
      </button>
      <button
        v-else-if="examination.is_subscription_item"
        class="mt-2 px-8 py-2 rounded bg-blue-600 text-blue-50 max-w-max shadow-sm hover:opacity-75"
        @click="handlePurchaseExam">
        無料で購入する
      </button>
      <button
        v-else
        class="mt-2 px-8 py-2 rounded bg-blue-600 text-blue-50 max-w-max shadow-sm hover:opacity-75"
        @click="handlePurchaseExam">
        購入する
      </button>
    </div>

    <!-- 試験タイプごとのボタン -->
    <div
      v-for="(typeName, type) in examinationTypes"
      :key="type">
      <div class="flex sp:text-sm items-center justify-between overflow-hidden sm:rounded-none p-2">
        <p v-if="examination.is_subscription_item">
          {{ typeName }}
        </p>
        <p v-else>{{ typeName }}{{ examinationTypesSpec[type] }}</p>

        <div v-if="isPurchased(examination.id)">
          <!-- 実施済み -->
          <div
            v-if="isFinished(examination.id, Number(type)) || isExpired(examination, Number(type))"
            class="bg-red-500 text-white py-2.5 px-10 sp:px-7 rounded-lg opacity-50 cursor-not-allowed">
            実施済み
          </div>
          <!-- 再試験（2024年度以前のみ） -->
          <button
            v-else-if="isAvailableRetry(examination) && fiscalYearStart(examination.reception_start)"
            class="bg-blue-600 text-white py-2.5 px-10 sp:px-7 rounded-lg"
            @click="handleConfirm(examination, Number(type))">
            <div>開始する</div>
            <div class="text-sm">(集計対象外)</div>
          </button>
          <!-- 再開 -->
          <button
            v-else-if="isInProgress(examination.id, Number(type)) && !isExpired(examination, Number(type))"
            class="bg-green-600 lg:hover:bg-green-400 active:bg-green-400 duration-300 text-white py-2.5 px-10 sp:px-7 rounded-lg"
            type="button"
            @click="handleResume(examination, Number(type))">
            再開する
          </button>
          <!-- 試験開始前 -->
          <button
            v-else-if="isBeforeDate(examination.start_date)"
            class="bg-gray-500 text-white py-2.5 px-10 sp:px-7 rounded-lg opacity-50 cursor-not-allowed">
            試験開始前
          </button>
          <!-- 特権ユーザー -->
          <button
            v-else-if="isPrivilege() && !isInProgress(examination.id, Number(type)) && !isFinished(examination.id, Number(type))"
            class="bg-blue-600 lg:hover:bg-blue-400 active:bg-blue-400 duration-300 text-white py-2.5 px-10 sp:px-7 rounded-lg"
            type="button"
            @click="handleConfirm(examination, Number(type))">
            開始する
          </button>
          <!-- 実施期間終了済 -->
          <button
            v-else-if="isOveredDate(examination.reception_end)"
            class="bg-red-500 text-white py-2.5 px-10 sp:px-7 rounded-lg opacity-50 cursor-not-allowed">
            実施期間終了済
          </button>
          <!-- 通常開始 -->
          <button
            v-else-if="!isInProgress(examination.id, Number(type)) && !isFinished(examination.id, Number(type))"
            class="bg-blue-600 lg:hover:bg-blue-400 active:bg-blue-400 duration-300 text-white py-2.5 px-10 sp:px-7 rounded-lg"
            type="button"
            @click="handleConfirm(examination, Number(type))">
            開始する
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import moment from 'moment'
import { reactive } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import formatDate from '@/actions/format/date'
import formatPrice from '@/actions/format/formatPrice'

const props = defineProps({
  examination: {
    type: Object,
    required: true,
  },
  examinationTypes: {
    type: Object,
    required: true,
  },
  examinationTypesSpec: {
    type: Object,
    required: true,
  },
  purchases: {
    type: Array,
    required: true,
  },
  progresses: {
    type: Array,
    required: true,
  },
  userData: {
    type: Object,
    required: true,
  },
  limitTimes: {
    type: Object,
    required: true,
  },
  fiscalYearStart: {
    type: String,
    required: true,
  },
})

// 選択模試表示ブロックのロゴURL
const logoUrl = '/images/Exam/Exam-icon.svg'

const emit = defineEmits(['purchase-exam', 'confirm', 'resume'])

const handlePurchaseExam = () => {
  emit('purchase-exam', props.examination)
}

const handleConfirm = (examination, type) => {
  emit('confirm', examination, type)
}

const handleResume = (examination, type) => {
  emit('resume', examination, type)
}

// 管理画面に設定するよう
// ヘルパー関数 変数名を変更
// TODO 現在集計日時はconfig/services.phpに設定しているが、動的に変更
const fiscalYearStart = (date) => {
  const FY2024 = moment(props.fiscalYearStart)
  const momentDate = moment(date)
  return momentDate.isBefore(FY2024)
}

const isPurchased = (id) => props.purchases.filter((purchase) => purchase.exam_id === id).length

const isOutOfTerm = () => {
  const now = moment()
  const receptionStart = moment(props.examination.reception_start)
  const receptionEnd = moment(props.examination.reception_end)
  return now.isBefore(receptionStart) || now.isAfter(receptionEnd)
}

const isInProgress = (id, type) => props.progresses.filter(
  (progress) => progress.examination_id === id
      && progress.examination_type === type
      && progress.started_at !== null
      && progress.finished_at === null,
).length

const isFinished = (id, type) => props.progresses.filter(
  (progress) => progress.examination_id === id
      && progress.examination_type === type
      && progress.finished_at !== null,
).length

const isBeforeDate = (data) => {
  const now = moment()
  const momentDate = moment(data)
  return now.isBefore(momentDate)
}

const isOveredDate = (data) => {
  const now = moment()
  const momentDate = moment(data)
  return now.isAfter(momentDate)
}

const isPrivilege = () => props.userData.is_privilege

const isAllFinished = (id) => {
  const progresses = props.progresses.filter(
    (progress) => progress.examination_id === id,
  )

  if (progresses.length < 3) {
    return false
  }
  for (const element of progresses) {
    if (!element.finished_at) {
      return false
    }
  }
  return true
}

const isAvailableRetry = (examination) => {
  if (
    isOveredDate(examination.reception_end)
    || !isOveredDate(examination.start_date)
  ) {
    return false
  }

  if (isOveredDate(examination.result_announcement_date)) {
    return true
  }

  if (
    examination.result_announcement_date == null
    && isAllFinished(examination.id)
  ) {
    return true
  }
  return false
}

const isExpired = (examination, type) => {
  const now = moment().format('YYYY-MM-DD HH:mm:ss')

  let limitSeconds = 0
  if (examination.is_subscription_item) {
    limitSeconds = props.limitTimes[type] / 2
  } else {
    limitSeconds = props.limitTimes[type]
  }

  // 試験の終了時間をその場で計算
  const conversionMinutes = limitSeconds / 60
  const progressObj = props.progresses.find(
    (progress) => progress.examination_id === examination.id
      && progress.examination_type === type,
  )
  if (progressObj == null) {
    return false
  }

  const finishExaminationTime = moment(progressObj.started_at).add(conversionMinutes, 'm').format()

  if (finishExaminationTime === 0 || !finishExaminationTime) {
    return false
  }

  const diffSeconds = moment(finishExaminationTime).diff(moment(now))
  if (diffSeconds < 0) {
    const json = localStorage.getItem('answers')
    const answerList = json != null ? JSON.parse(json) : []
    const form = reactive(
      Inertia.form({
        answerList,
        examination,
        type,
      }),
    )
    form.post(route('client-examinations.finish'))
    return true
  }
  return false
}
</script>
